import Swal from 'sweetalert2';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AvantoLambdaService } from 'src/app/services/avanto-lambda.service';
import { AvantoRoutingService } from 'src/app/services/avanto-routing.service';
import { AvantoSessionService } from 'src/app/services/avanto-session.service';

declare const Square:any;

/**
 * Apple Pay and Google Pay buttons for restaurants paid through Square.
 * They live on the checkout page, not inside the payments.avantoeats.com iframe, because Apple Pay only works on the page shown in the address bar.
 * The payment goes to the same backend step the Square payment page uses, which validates the order before charging it.
 */
@Component({
  selector: 'app-square-wallet-buttons',
  templateUrl: './square-wallet-buttons.component.html',
  styleUrls: ['./square-wallet-buttons.component.scss']
})
export class SquareWalletButtonsComponent implements OnInit, OnChanges, OnDestroy {

  static readonly SQUARE_SDK_URL         = "https://web.squarecdn.com/v1/square.js";
  static readonly SQUARE_SANDBOX_SDK_URL = "https://sandbox.web.squarecdn.com/v1/square.js";
  static readonly MAX_NUM_RETRIES        = 2;
  private static sdkLoadingPromise:Promise<void>|null = null;

  @Input() sessionId:string;
  @Input() total:number;
  //Shown on the wallet sheet ("Pay <merchantName>")
  @Input() merchantName:string;
  @Input() showGooglePay:boolean;
  @Input() showApplePay:boolean;
  @Input() disabled:boolean;
  //TRUE while a wallet payment is being charged. The checkout page shows its loading overlay.
  @Output() processingChange = new EventEmitter<boolean>();

  @ViewChild('googlePayContainer') googlePayContainer:ElementRef;

  isApplePayAvailable  = false;
  isGooglePayAvailable = false;
  //A placeholder is shown while Square checks which wallets this device supports
  private isLoadingSquare    = true;
  private numWalletsStarting = 0;

  private payments:any;
  private paymentRequest:any;
  private applePay:any;
  private googlePay:any;
  private isApplePayStarted  = false;
  private isGooglePayStarted = false;
  private isProcessing = false;
  private isDestroyed  = false;

  constructor(private avantoLambda:AvantoLambdaService, private avantoSession:AvantoSessionService,
              private avantoRoute:AvantoRoutingService, private changeDetector:ChangeDetectorRef) { }

  ngOnInit() {
    this.initialize();
  }

  get isLoading():boolean {
    return this.isLoadingSquare || this.numWalletsStarting > 0;
  }

  get hasVisibleWallet():boolean {
    return (this.showApplePay && this.isApplePayAvailable) || (this.showGooglePay && this.isGooglePayAvailable);
  }

  ngOnChanges(changes:SimpleChanges) {
    if(this.paymentRequest == null){
      return;
    }
    if(changes['total'] || changes['merchantName']){
      this.paymentRequest.update({ total: this.getPaymentRequestTotal() });
    }
    //The order type changed (pickup/delivery), and it may have a different wallet turned on
    if(changes['showGooglePay'] || changes['showApplePay']){
      this.startWallets();
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    if(this.applePay != null){
      this.applePay.destroy().catch(() => {});
    }
    if(this.googlePay != null){
      this.googlePay.destroy().catch(() => {});
    }
  }


  //====================== SETUP ======================\\
  /** Wallets that can't start (unsupported device, domain not registered with Square...) stay hidden. Card payments are not affected. */
  private async initialize() {
    try{
      const response:any = await this.avantoLambda.getSquarePaymentInformation(this.sessionId).toPromise();
      const paymentInformation = response.json().data;
      const applicationId:string = paymentInformation.applicationId;
      const locationId:string    = paymentInformation.locationId;

      await SquareWalletButtonsComponent.loadSquareSdk(applicationId.startsWith("sandbox-"));
      if(this.isDestroyed){
        return;
      }
      this.payments       = Square.payments(applicationId, locationId);
      this.paymentRequest = this.payments.paymentRequest({
        countryCode: 'CA',
        currencyCode: 'CAD',
        total: this.getPaymentRequestTotal()
      });
    }catch(ex){
      console.warn("Apple Pay and Google Pay are not available", ex);
      this.isLoadingSquare = false;
      return;
    }
    this.startWallets();
    this.isLoadingSquare = false;
  }

  private startWallets() {
    if(this.showApplePay && !this.isApplePayStarted){
      this.isApplePayStarted = true;
      this.trackWalletStart(this.startApplePay());
    }
    if(this.showGooglePay && !this.isGooglePayStarted){
      this.isGooglePayStarted = true;
      this.trackWalletStart(this.startGooglePay());
    }
  }

  /** The wallet start methods never reject. Failures just leave the wallet hidden. */
  private trackWalletStart(walletStart:Promise<void>) {
    this.numWalletsStarting++;
    walletStart.finally(() => {
      this.numWalletsStarting--;
    });
  }

  private async startApplePay() {
    try{
      this.applePay = await this.payments.applePay(this.paymentRequest);
      this.isApplePayAvailable = !this.isDestroyed;
    }catch(ex){
      console.warn("Apple Pay is not available", ex);
    }
  }

  private async startGooglePay() {
    try{
      this.googlePay = await this.payments.googlePay(this.paymentRequest);
    }catch(ex){
      console.warn("Google Pay is not available", ex);
      return;
    }
    if(this.isDestroyed){
      return;
    }

    try{
      //The container must be visible before Google draws its button inside it
      this.isGooglePayAvailable = true;
      this.changeDetector.detectChanges();
      await this.googlePay.attach(this.googlePayContainer.nativeElement, { buttonColor: 'black', buttonSizeMode: 'fill', buttonType: 'long' });
    }catch(ex){
      this.isGooglePayAvailable = false;
      console.warn("Google Pay button could not be displayed", ex);
    }
  }

  private getPaymentRequestTotal() {
    return {
      amount: Number(this.total).toFixed(2),
      label: this.merchantName || "Total"
    };
  }

  /** Loads Square's script once, the first time a checkout page needs it */
  private static loadSquareSdk(isSandbox:boolean):Promise<void> {
    if(SquareWalletButtonsComponent.sdkLoadingPromise == null){
      SquareWalletButtonsComponent.sdkLoadingPromise = new Promise<void>((resolve, reject) => {
        const script   = document.createElement("script");
        script.src     = isSandbox ? SquareWalletButtonsComponent.SQUARE_SANDBOX_SDK_URL : SquareWalletButtonsComponent.SQUARE_SDK_URL;
        script.onload  = () => resolve();
        script.onerror = () => {
          SquareWalletButtonsComponent.sdkLoadingPromise = null;
          reject(new Error("Could not load Square's script"));
        };
        document.head.appendChild(script);
      });
    }
    return SquareWalletButtonsComponent.sdkLoadingPromise;
  }


  //====================== PAYMENT ======================\\
  payWithApplePay() {
    if(this.disabled || this.isProcessing){
      return;
    }
    //Apple requires tokenize() to be called right away, with nothing awaited before it
    this.payWithWallet(this.applePay.tokenize());
  }

  payWithGooglePay() {
    if(this.disabled || this.isProcessing){
      return;
    }
    this.payWithWallet(this.googlePay.tokenize());
  }

  /**
   * Square returns status "OK" with a one-time token. "Cancel" means the customer closed the wallet sheet.
   * The loading overlay goes up right away: it sits behind the wallet sheet, and after the customer approves,
   * it covers the few seconds Square takes to return the token.
   */
  private async payWithWallet(tokenizePromise:Promise<any>) {
    this.isProcessing = true;
    this.processingChange.emit(true);

    let token:string;
    let last4DigitsOfCard:string|null = null;
    try{
      const tokenResult = await tokenizePromise;
      if(tokenResult.status != "OK"){
        this.stopProcessing();
        if(tokenResult.status != "Cancel"){
          console.error("Wallet tokenization failed", tokenResult);
          Swal.fire("Payment not completed", "We could not complete the payment with your wallet. Please try again or use a card.", "error");
        }
        return;
      }
      token = tokenResult.token;
      if(tokenResult.details != null && tokenResult.details.card != null && tokenResult.details.card.last4 != null){
        last4DigitsOfCard = tokenResult.details.card.last4;
      }
    }catch(ex){
      this.stopProcessing();
      console.error("Wallet tokenization failed", ex);
      Swal.fire("Payment not completed", "We could not complete the payment with your wallet. Please try again or use a card.", "error");
      return;
    }

    this.chargePayment(token, last4DigitsOfCard, null);
  }

  private stopProcessing() {
    this.isProcessing = false;
    this.processingChange.emit(false);
  }

  /** Timeouts are retried with the same idempotency key (isRetry), exactly like the Square payment page */
  private chargePayment(token:string, last4DigitsOfCard:string|null, numberOfRetriesLeft:number|null) {
    this.avantoLambda.processSquarePayment(this.sessionId, token, last4DigitsOfCard, numberOfRetriesLeft != null).toPromise()
      .then(() => {
        this.completeOrder();
      })
      .catch((ex) => {
        const errorMessage = this.getErrorMessage(ex, "Error while processing the payment.");
        if(errorMessage.toLowerCase() == "timeout" && (numberOfRetriesLeft == null || numberOfRetriesLeft > 0)){
          const retriesLeft = (numberOfRetriesLeft == null) ? SquareWalletButtonsComponent.MAX_NUM_RETRIES : numberOfRetriesLeft;
          console.log("RETRYING TRANSACTION " + retriesLeft + " MORE TIMES");
          setTimeout(() => { this.chargePayment(token, last4DigitsOfCard, retriesLeft - 1); }, 1000);
          return;
        }

        console.error(ex);
        this.stopProcessing();
        Swal.fire("Payment not completed", errorMessage, "error");
      });
  }

  /** Same ending as the Square payment page */
  private completeOrder() {
    this.avantoSession.clearSession()
      .catch(() => {
        this.avantoSession.removeSessionFromCookie();
      })
      .finally(() => {
        this.avantoRoute.goToOrderCompletePage();
      });
  }

  /** The backend answers errors with { error: "message" } */
  private getErrorMessage(ex:any, defaultMessage:string):string {
    let error:any = null;
    try{
      error = ex.json().error;
    }catch(parseError){
      return defaultMessage;
    }
    if(error != null && error.errMsg != null){
      return error.errMsg;
    }
    return (typeof error == "string") ? error : defaultMessage;
  }
}
